/**
 * Click handler helpers for CardBody component
 * Contains reusable click handler functions
 */

import moment from "moment-timezone";
import { isEntryUnlocked } from "../utils/cardBodyDateHelpers.js";

/**
 * Create a click handler for entering edit mode
 * This pattern is repeated many times throughout CardBody
 */
export const createEditModeClickHandler = ({
  group,
  getLockHoursHandler,
  globalConfig,
  editedId,
  effectiveSetEditedId,
  initializeEditStateFromGroup,
  setClickId,
  toggleGroup,
  groupIndex,
  handleOpenPopup,
  setIsTimeInputActive,
  setProjectEditDropdownOpen,
  setTagEditDropdownOpen,
  setTaskNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setSprintNameEdit,
  setSprintIdEdit,
  setDateEdit,
  setTagIdEdit,
  setTagEdit,
  setTagQuery,
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeTemp,
  setEndTimeTemp,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setUFormatedTime,
  setUDuration,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
}) => {
  return () => {
    const isLocked = getLockHoursHandler(
      moment(group.endTime)
        .tz(globalConfig.getTimeZone())
        .format("YYYY-MM-DD HH:mm:ss"),
      globalConfig.getTimeZone(),
      "YYYY-MM-DD HH:mm:ss"
    );

    if (isLocked === true) {
      if (group.entries.length === 1) {
        // Prevent re-initializing edit state if already editing this entry
        if (group._id !== editedId) {
          initializeEditStateFromGroup({
            group,
            effectiveSetEditedId,
            setProjectEditDropdownOpen,
            setTagEditDropdownOpen,
            setTaskNameEdit,
            setProjectIdEdit,
            setProjectNameEdit,
            setSprintNameEdit,
            setSprintIdEdit,
            setDateEdit,
            setTagIdEdit,
            setTagEdit,
            setTagQuery,
            setStartTimeEdit,
            setEndTimeEdit,
            setStartTimeTemp,
            setEndTimeTemp,
            setStartTimeEdit24,
            setEndTimeEdit24,
            setClientEdit,
            setClientIdEdit,
            setBillableEdit,
            setUFormatedTime,
            setUDuration,
            setUStartTimeError,
            setUEndTimeError,
            setUFormatedTimeError,
            globalConfig,
          });
        }
      } else {
        toggleGroup(groupIndex);
      }
    } else {
      if (group.entries.length === 1) {
        setClickId(group._id);
      } else {
        toggleGroup(groupIndex);
        setClickId(null);
      }
    }
  };
};

/**
 * Create a click handler for project dropdown toggle
 */
export const createProjectDropdownToggleHandler = ({
  setClickId,
  group,
  projectEditDropdownOpen,
  setProjectEditDropdownOpen,
  setProjectStatusFilter,
  setFilter,
  setSelectedClients,
  setSelectedProject,
  setTagEditDropdownOpen,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
}) => {
  return () => {
    setClickId(group.entries.length === 1 ? group._id : null);
    setProjectEditDropdownOpen(!projectEditDropdownOpen);
    setProjectStatusFilter("Active");
    setFilter("");
    setSelectedClients(new Set());
    setSelectedProject(null);
    setTagEditDropdownOpen(false);
    if (setUStartTimeError) setUStartTimeError(false);
    if (setUEndTimeError) setUEndTimeError(false);
    if (setUFormatedTimeError) setUFormatedTimeError(false);
  };
};

/**
 * Create a click handler for tag dropdown toggle
 */
export const createTagDropdownToggleHandler = ({
  tagDropdownContentRef,
  tagEditDropdownOpen,
  setTagEditDropdownOpen,
  setProjectEditDropdownOpen,
}) => {
  return (e) => {
    // Don't toggle if clicking inside the dropdown
    if (tagDropdownContentRef.current && tagDropdownContentRef.current.contains(e.target)) {
      return;
    }
    setTagEditDropdownOpen(!tagEditDropdownOpen);
    setProjectEditDropdownOpen(false);
  };
};

/**
 * Create a click handler for client selection in project dropdown
 */
export const createClientToggleHandler = ({
  client,
  filter,
  projectStatusFilter,
  filteredClientData,
  selectedClients,
  setSelectedClients,
  setClickId,
  group,
}) => {
  return () => {
    setClickId(group.entries.length === 1 ? group._id : null);
    if (
      client.projects.filter(
        (project) =>
          project.project_name.toLowerCase().includes(filter.toLowerCase()) &&
          (projectStatusFilter === "All"
            ? true
            : projectStatusFilter === "Active"
              ? project.is_active === true
              : project.is_active === false)
      ).length > 0
    ) {
      // Toggle the client in the Set
      const newSelectedClients = new Set(selectedClients);
      if (selectedClients.has(client)) {
        newSelectedClients.delete(client);
      } else {
        newSelectedClients.add(client);
      }
      setSelectedClients(newSelectedClients);
    }
  };
};

/**
 * Create a click handler for project selection
 */
export const createProjectSelectHandler = ({
  project,
  client,
  setSprintIdEdit,
  setSprintNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setProjectEditDropdownOpen,
}) => {
  return (e) => {
    e.stopPropagation();
    setSprintIdEdit("");
    setSprintNameEdit("");
    setProjectIdEdit(project._id);
    setProjectNameEdit(project.project_name);
    setClientEdit(client.client_name);
    setClientIdEdit(client._id);
    setBillableEdit(project.is_billable);
    setProjectEditDropdownOpen(false);
  };
};

/**
 * Create a click handler for sprint selection
 */
export const createSprintSelectHandler = ({
  sprint,
  project,
  client,
  setSprintIdEdit,
  setSprintNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setProjectEditDropdownOpen,
}) => {
  return (e) => {
    e.stopPropagation();
    setSprintIdEdit(sprint._id);
    setSprintNameEdit(sprint.sprint_name);
    setProjectIdEdit(project._id);
    setProjectNameEdit(project.project_name);
    setClientEdit(client.client_name);
    setClientIdEdit(client._id);
    setBillableEdit(project.is_billable);
    setProjectEditDropdownOpen(false);
  };
};

/**
 * Create a click handler for tag selection
 */
export const createTagSelectHandler = ({
  tag,
  setTagEdit,
  setTagIdEdit,
  setTagQuery,
  setTagEditDropdownOpen,
}) => {
  return (e) => {
    e.stopPropagation();
    setTagEdit(tag.tag_name);
    setTagIdEdit(tag._id);
    setTagQuery("");
    setTagEditDropdownOpen(false);
  };
};

/**
 * Create a click handler for actions dropdown toggle
 */
export const createActionsDropdownToggleHandler = ({
  group,
  getLockHoursHandler,
  globalConfig,
  handleDropdownToggleFunc,
  setClickId,
}) => {
  return () => {
    if (
      getLockHoursHandler(
        moment(group.endTime)
          .tz(globalConfig.getTimeZone())
          .format("YYYY-MM-DD HH:mm:ss"),
        globalConfig.getTimeZone(),
        "YYYY-MM-DD HH:mm:ss"
      ) === true
    ) {
      handleDropdownToggleFunc(group._id);
    } else {
      setClickId(group.entries.length === 1 ? group._id : null);
    }
  };
};

/**
 * Create a click handler for display sections (time, date, duration)
 * Handles the common pattern of checking unlock status and either
 * entering edit mode or toggling merged groups
 */
export const createDisplaySectionClickHandler = ({
  group,
  row,
  getLockHoursHandler,
  globalConfig,
  setClickId,
  initializeEditStateFromGroup,
  effectiveSetEditedId,
  setProjectEditDropdownOpen,
  setTagEditDropdownOpen,
  setTaskNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setSprintNameEdit,
  setSprintIdEdit,
  setDateEdit,
  setTagIdEdit,
  setTagEdit,
  setTagQuery,
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeTemp,
  setEndTimeTemp,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setUFormatedTime,
  setUDuration,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  toggleGroup,
  groupIndex,
  handleOpenPopup,
  useMomentFormat = false,
  onAfterInitialize,
}) => {
  return () => {
    const currentItem = row || group;
    if (!currentItem) {
      return;
    }

    const isUnlocked = isEntryUnlocked(currentItem.endTime, globalConfig, getLockHoursHandler);

    // Handle row clicks (individual entries within merged groups)
    if (row) {
      if (isUnlocked) {
        if (initializeEditStateFromGroup) {
          initializeEditStateFromGroup({
            group: row,
            effectiveSetEditedId,
            setProjectEditDropdownOpen,
            setTagEditDropdownOpen,
            setTaskNameEdit,
            setProjectIdEdit,
            setProjectNameEdit,
            setSprintNameEdit,
            setSprintIdEdit,
            setDateEdit,
            setTagIdEdit,
            setTagEdit,
            setTagQuery,
            setStartTimeEdit,
            setEndTimeEdit,
            setStartTimeTemp,
            setEndTimeTemp,
            setStartTimeEdit24,
            setEndTimeEdit24,
            setClientEdit,
            setClientIdEdit,
            setBillableEdit,
            setUFormatedTime,
            setUDuration,
            setUStartTimeError,
            setUEndTimeError,
            setUFormatedTimeError,
            globalConfig,
          });
        }
        if (onAfterInitialize) {
          onAfterInitialize();
        }
      } else {
        if (handleOpenPopup) {
          handleOpenPopup();
        }
        if (setClickId) {
          setClickId(row._id);
        }
      }
      return;
    }

    // Handle group clicks
    if (group) {
      if (isUnlocked) {
        // For merged groups (entries.length > 1), toggle to expand/collapse
        // For single entry groups (entries.length === 1), enter edit mode
        if (group.entries.length === 1) {
          if (initializeEditStateFromGroup) {
            initializeEditStateFromGroup({
              group,
              effectiveSetEditedId,
              setProjectEditDropdownOpen,
              setTagEditDropdownOpen,
              setTaskNameEdit,
              setProjectIdEdit,
              setProjectNameEdit,
              setSprintNameEdit,
              setSprintIdEdit,
              setDateEdit,
              setTagIdEdit,
              setTagEdit,
              setTagQuery,
              setStartTimeEdit,
              setEndTimeEdit,
              setStartTimeTemp,
              setEndTimeTemp,
              setStartTimeEdit24,
              setEndTimeEdit24,
              setClientEdit,
              setClientIdEdit,
              setBillableEdit,
              setUFormatedTime,
              setUDuration,
              setUStartTimeError,
              setUEndTimeError,
              setUFormatedTimeError,
              globalConfig,
              useMomentFormat,
            });
          }
          if (onAfterInitialize) {
            onAfterInitialize();
          }
        } else {
          // Toggle group for merged entries
          if (toggleGroup && groupIndex !== undefined) {
            toggleGroup(groupIndex);
          }
        }
      } else {
        // Entry is locked
        if (group.entries.length === 1) {
          if (setClickId) {
            setClickId(group._id);
          }
        } else {
          // For merged groups, still toggle even if locked
          if (toggleGroup && groupIndex !== undefined) {
            toggleGroup(groupIndex);
          }
          if (setClickId) {
            setClickId(null);
          }
        }
      }
    }
  };
};

/**
 * Create a click handler for ResumeButton
 * Handles the common pattern of resuming time tracking
 */
export const createResumeButtonClickHandler = ({
  resumeTimeTracking,
  resumeLoading,
  customeTime,
  group,
  row,
}) => {
  return () => {
    if (!resumeLoading && !customeTime) {
      const item = row || group;
      if (!item) {
        return;
      }

      const tagName = (item.tag && item.tag.tag_name) || item.tag?.tag_name;
      const tagId = (item.tag && item.tag._id) || item.tag?._id;
      const duration = row ? row.duration : group.totalDuration;

      resumeTimeTracking(
        item.task,
        item.project._id,
        item.project.project_name,
        tagName,
        tagId,
        item.client,
        item.project.client_name,
        item.date,
        item.startTime,
        item.endTime,
        duration,
        item.sprint ? item.sprint.sprint_name : null,
        item.sprint ? item.sprint._id : null,
        item._id,
        item.billable
      );
    }
  };
};
